import { BaseSerializer } from '/src/app/core/serializers/baseSerializer';

class AppDataSerializer extends BaseSerializer {
  deserialize(raw) {
    const serverRes = raw.data || raw.error;
    const success = serverRes.statusCode == 200 || serverRes.statusCode == 404;
    if (!success) {
      return {
        statusCode: serverRes.statusCode || raw.status || 500,
        success: false,
        message:
          (serverRes.message || 'Erreur lors de la récupération') +
          ` Code d'erreur: ADS_ERR_${
            serverRes.statusCode || raw.status || '1000'
          }`,
        data: null
      };
    }

    const savedData = serverRes?.data?.appInfo;

    return {
      statusCode: 200,
      success: true,
      message: 'App data retrieved successfully',
      data: {
        general: this.deserializeGeneral(savedData?.general),
        contacts: this.deserializeContacts(savedData?.contacts),
        rates: this.deserializeRates(savedData?.rates),
        paymentOtpUssdCode: this.deserializePaymentOtpUssdCode(
          savedData?.paymentOtpUssdCode
        ),
        addresses: this.deserializeAddresses(savedData?.addresses),
        paymentProviders: savedData?.paymentProviders || {},
        manualOperationsInstructions:
          savedData?.manualOperationsInstructions || [],
        amountLimits: this.deserializeAmountLimits(savedData?.amountLimits),
        fees: this.deserializeFees(savedData?.fees),
        serviceAvailability: this.deserializeServiceAvailability(savedData?.serviceAvailability),
        otpSettings: savedData?.otpSettings || {}
      }
    };
  }

  deserializeServiceAvailability(saved) {
    const countries = this.getCountries();
    const operationKeys = [
      'fund1xBet', 'withdrawal1xBet',
      'fundBetWinner', 'withdrawalBetWinner',
      'fund1Win', 'withdrawal1Win',
      'fundMelbet', 'withdrawalMelbet',
      'fundEightStarz', 'withdrawalEightStarz',
      'buyUsdt', 'sellUsdt'
    ];
    const result = {};
    countries.forEach(country => {
      result[country] = {};
      operationKeys.forEach(key => {
        result[country][key] = saved?.[country]?.[key] ?? true;
      });
    });
    return result;
  }

  getCountries() {
    return ['BJ', 'BF', 'CI', 'GA', 'ML', 'NE', 'SN', 'TG'];
  }

  deserializeGeneral(general) {
    return {
      whatsappBotLink: general?.whatsappBotLink || '',
      telegramBotLink: general?.telegramBotLink || '',
      withdrawVideoLink: general?.withdrawVideoLink || '',
      withdrawTutorialLink: general?.withdrawTutorialLink || '',
      iphoneInstallationVideoLink: general?.iphoneInstallationVideoLink || '',
      rechargeVideoLink: general?.rechargeVideoLink || '',
      rechargeTutorialLink: general?.rechargeTutorialLink || '',
      facebookLink: general?.facebookLink || '',
      instagramLink: general?.instagramLink || '',
      tiktokLink: general?.tiktokLink || '',
      websiteLink: general?.websiteLink || '',
      affiliationDetailText: general?.affiliationDetailText || '',
      whatsappComplaintLink: general?.whatsappComplaintLink || '',
      passwordResetUrl: general?.passwordResetUrl || '',
      faqUrl: general?.faqUrl || '',
      policyUrl: general?.policyUrl || '',
      cguUrl: general?.cguUrl || '',
      email: general?.email || '',
      snapchatLink: general?.snapchatLink || '',
      userCurrency: general?.userCurrency || '',
      minAmountForTransaction: general?.minAmountForTransaction || '',
      // Map server 'Deposit/Withdraw' to app 'Buy/Sell'
      minAmountBuyXbet: general?.minAmountBuyXbet || general?.minAmountDepositXbet || '',
      minAmountSellXbet: general?.minAmountSellXbet || general?.minAmountWithdrawXbet || '',
      minAmountBuyBetwinner: general?.minAmountBuyBetwinner || general?.minAmountDepositBetwinner || '',
      minAmountSellBetwinner: general?.minAmountSellBetwinner || general?.minAmountWithdrawBetwinner || '',
      minAmountBuyPm: general?.minAmountBuyPm || '',
      minAmountSellPm: general?.minAmountSellPm || '',
      minAmountBuyUsdt: general?.minAmountBuyUsdt || '',
      minAmountSellUsdt: general?.minAmountSellUsdt || '',
      telegramLink: general?.telegramLink || '',
      minAmountBuyOneWin: general?.minAmountBuyOneWin || general?.minAmountDepositOneWin || '',
      minAmountSellOneWin: general?.minAmountSellOneWin || general?.minAmountWithdrawOneWin || '',
      minAmountFundWallet: general?.minAmountFundWallet || general?.minAmountDepositWallet || '',
      minAmountwithdrawalWallet: general?.minAmountwithdrawalWallet || general?.minAmountWithdrawalWallet || '',
      whatsappAssistanceLink: general?.whatsappAssistanceLink || '',
      telegramAssistanceLink: general?.telegramAssistanceLink || '',
      netflixDownloadUrl: general?.netflixDownloadUrl || '',
      crunchyrolDownloadUrl: general?.crunchyrolDownloadUrl || '',
      xbetDownloadUrl: general?.xbetDownloadUrl || '',
      betwinnerDownloadUrl: general?.betwinnerDownloadUrl || '',
      oneWinDownloadUrl: general?.oneWinDownloadUrl || '',
      primeVideoDownloadUrl: general?.primeVideoDownloadUrl || '',
      spotifyDownloadUrl: general?.spotifyDownloadUrl || '',
      nordVPNDownloadUrl: general?.nordVPNDownloadUrl || '',
      privateVPNDownloadUrl: general?.privateVPNDownloadUrl || '',
      cyberGhostDownloadUrl: general?.cyberGhostDownloadUrl || '',
      whatsappLink: general?.whatsappLink || '',
      mailWhatsapp: general?.mailWhatsapp || '',
      minAmountBuyEightStarz: general?.minAmountBuyEightStarz || general?.minAmountDepositEightStarz || 0,
      minAmountSellEightStarz: general?.minAmountSellEightStarz || general?.minAmountWithdrawEightStarz || 0,
      minAmountSellMelbet: general?.minAmountSellMelbet || general?.minAmountWithdrawMelbet || 0,
      minAmountBuyMelbet: general?.minAmountBuyMelbet || general?.minAmountDepositMelbet || 0,
      specificTutorials: general?.specificTutorials || {}
    };
  }

  deserializeContacts(savedContacts) {
    const countries = [...this.getCountries(), 'ALL'];
    const contacts = {};
    countries.forEach((country) => {
      contacts[country] = {
        whatsappPhoneNumber: savedContacts?.[country]?.whatsappPhoneNumber || '',
        whatsappPhoneNumberComplaint:
          savedContacts?.[country]?.whatsappPhoneNumberComplaint || '',
        whatsappLink: savedContacts?.[country]?.whatsappLink || '',
        whatsappChatLink: savedContacts?.[country]?.whatsappChatLink || '',
        telegramLink: savedContacts?.[country]?.telegramLink || ''
      };
    });
    return contacts;
  }

  deserializeRates(savedRates) {
    const countries = this.getCountries();
    const rates = {};
    countries.forEach((country) => {
      rates[country] = {
        buyRates: {
          USDT: {
            XOF: savedRates?.[country]?.buyRates?.USDT?.XOF || 0,
            XAF: savedRates?.[country]?.buyRates?.USDT?.XAF || 0
          }
        },
        sellRates: {
          USDT: {
            XOF: savedRates?.[country]?.sellRates?.USDT?.XOF || 0,
            XAF: savedRates?.[country]?.sellRates?.USDT?.XAF || 0
          }
        }
      };
    });
    return rates;
  }

  deserializePaymentOtpUssdCode(savedUssd) {
    const countries = ['ML', 'BF', 'SN', 'CI'];
    const ussd = {};
    countries.forEach((country) => {
      ussd[country] = {
        text: savedUssd?.[country]?.text || '',
        ussdCode: savedUssd?.[country]?.ussdCode || ''
      };
    });
    return ussd;
  }

  deserializeAddresses(savedAddresses) {
    const countries = this.getCountries();
    const addresses = {};
    countries.forEach((country) => {
      const data = savedAddresses?.[country];
      addresses[country] = {
        usdtTrc20Address: data?.usdtTrc20Address || '',
        usdtErc20Address: data?.usdtErc20Address || '',
        usdtBep20Address: data?.usdtBep20Address || '',
        binanceAccountId: data?.binanceAccountId || '',
        betwinnerWithdrawAddress:
          data?.betwinnerWithdrawAddress ||
          data?.betwinnerWithdrawAddressString ||
          '',
        melbetWithdrawAddress:
          data?.melbetWithdrawAddress ||
          data?.melbetWithdrawAddressString ||
          '',
        eightStarzWithdrawAddress:
          data?.eightStarzWithdrawAddress ||
          data?.eightStarzWithdrawAddressString ||
          '',
        oneWinWithdrawAddress:
          data?.oneWinWithdrawAddress ||
          data?.oneWinWithdrawAddressString ||
          '',
        xbetWithdrawAddress:
          data?.xbetWithdrawAddress || data?.xbetWithdrawAddressString || ''
      };
    });
    return addresses;
  }

  deserializeAmountLimits(savedLimits) {
    const countries = this.getCountries();
    const limitTypes = ['minAmounts', 'maxAmounts', 'dailyLimits', 'monthlyLimits'];
    const fields = [
      'forTransaction',
      'depositWallet',
      'withdrawalWallet',
      'depositXbet',
      'withdrawXbet',
      'depositBetwinner',
      'withdrawBetwinner',
      'depositOneWin',
      'withdrawOneWin',
      'buyUsdt',
      'sellUsdt',
      'depositEightStarz',
      'withdrawEightStarz',
      'depositMelbet',
      'withdrawMelbet'
    ];

    const limits = {};
    countries.forEach((country) => {
      limits[country] = {};
      limitTypes.forEach((type) => {
        limits[country][type] = {};
        fields.forEach((field) => {
          limits[country][type][field] =
            savedLimits?.[country]?.[type]?.[field] || 0;
        });
      });
    });

    return limits;
  }

  deserializeFees(savedFees) {
    const countries = this.getCountries();
    const fees = {};
    countries.forEach((country) => {
      fees[country] = {
        phoneVerification: savedFees?.[country]?.phoneVerification || 100
      };
    });
    return fees;
  }

  serialize() {}
}

export const serializer = new AppDataSerializer();
